import type { DataDemandSource } from "./view-data-needs";

/**
 * Реестр «страница → какие общие данные ей нужны».
 *
 * Зачем: страницы видов уже грузят только нужное (см. view-data-needs.ts), а
 * обычные страницы по умолчанию запрашивали ВСЕ ~10 источников DataContext
 * веером — хотя странице нужны 1-2, а некоторым (реклама) — ноль (у них своя
 * загрузка). Это тормозило холодное открытие. Здесь каждая страница объявляет
 * только свои НЕбазовые источники.
 *
 * ВАЖНО:
 * - products(lite) теперь тоже грузится по спросу: товарная таблица запрашивает
 *   его сама. Здесь перечисляем только данные, нужные самой странице ещё до
 *   монтирования её блоков. Страница, которая сама читает `products` из
 *   useData() (без таблицы новой версии), ОБЯЗАНА указать "productsLite" —
 *   иначе список товаров у неё пустой: так «Склейки» с 17.08 показывали
 *   «0 групп» при связанных товарах (исправлено 2026-09-13).
 * - Страница, которой здесь НЕТ → грузит ВСЕ источники (безопасный откат, как
 *   было). Поэтому забыть страницу не страшно — она просто не ускорится.
 * - Добавили страницу или новую таблицу/виджет на странице, читающие общие
 *   данные (useData) — впишите её источники сюда. Защищено тестом
 *   tests/page-data-needs.test.ts и правилом в CLAUDE.md.
 *
 * Источники определены по фактическому useData() на странице и в её
 * компонентах (2026-06-15). Шапка приложения читает attrData+products, но
 * переживает их отсутствие (как на главной с пустым спросом), поэтому пустой
 * набор для страниц без работы с товарами безопасен.
 */
export const PAGE_DATA_NEEDS: Record<string, DataDemandSource[]> = {
  // Своя загрузка, общие данные не читают:
  "/ads": [],
  "/tasks": [],
  "/neurostudio": [],
  "/event-feed": [],
  "/new-finance": [],
  // Мокап плана поставки запрашивает источники по фактически
  // выбранным колонкам через getDataSourcesForColumn; все источники веером не нужны.
  "/supply-pult/planning-master-mock": [],
  // Читают часть общих данных:
  "/cost-prices": ["attrs"],
  "/v1/cost": ["attrs"],
  "/cost": [],
  // Сами читают products из useData() — без productsLite экран пустой.
  // suppliers: у цветовой полочки есть поле поставщика (SupplierCell) — без
  // источника введённое сохранялось, но не показывалось (поле снова пустое).
  "/product-groups": ["productsLite", "attrs", "suppliers"],
  "/assortment-map": ["productsLite", "attrs", "orders"],
  "/ab-tests": ["productsLite", "ads", "productsFull"],
  "/workspace": ["orders"],
};

/**
 * Нужды страницы по pathname или null, если страница не зарегистрирована
 * (тогда вызывающий грузит все источники — прежнее поведение).
 */
export function pageDataNeeds(pathname: string | null): DataDemandSource[] | null {
  if (!pathname) return null;
  const exact = PAGE_DATA_NEEDS[pathname];
  return exact ?? null;
}
